import {
  Stack,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import { Refresh, HealthAndSafety } from "@mui/icons-material";
import dayjs from "dayjs";
import { useState } from "react";
import { useHealthcheck } from "../hooks";

export const ServerDetails = ({ tenantId }: { tenantId: number }) => {
  const {
    healthcheckData,
    doHealthcheck: doHealthcheck,
    stopPolling,
    isHealthcheckLoading,
  } = useHealthcheck([tenantId]);

  const [isCachedData, setIsCachedData] = useState(true);

  const handleHealthcheck = async () => {
    stopPolling();
    await doHealthcheck();
    setIsCachedData(false);
  };

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="body1">Server Details:</Typography>
        <Button
          variant="outlined"
          startIcon={<HealthAndSafety />}
          onClick={handleHealthcheck}
          disabled={isHealthcheckLoading}
          size="small"
          color="primary"
        >
          {isHealthcheckLoading ? "Running..." : "Health Check"}
        </Button>
      </Box>
      {healthcheckData && Object.values(healthcheckData).length > 0 ? (
        Object.values(healthcheckData).map((server, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              ml: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {server.serverName}:
              </Typography>
              {isCachedData ? (
                <Chip
                  label="Cached"
                  color="warning"
                  variant="outlined"
                  size="small"
                  icon={<Refresh fontSize="small" />}
                />
              ) : !isHealthcheckLoading ? (
                <Chip
                  label="Online"
                  color="success"
                  variant="outlined"
                  size="small"
                />
              ) : (
                <Chip
                  label="Checking..."
                  color="secondary"
                  variant="outlined"
                  size="small"
                  icon={<CircularProgress size={16} color="secondary" />}
                />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Last checked:
              </Typography>
              <Typography variant="caption">
                {dayjs(server.lastHealthcheckAt).format("MMM D, YYYY h:mm A")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                App Name:
              </Typography>
              <Typography variant="caption">{server.appName}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Version:
              </Typography>
              <Typography variant="caption">{server.appVersion}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Recipe:
              </Typography>
              <Typography variant="caption">{server.recipeVersion}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                App Updated:
              </Typography>
              <Typography variant="caption">
                {dayjs(server.appLastUpdatedAt).format("MMM D, YYYY h:mm A")}
              </Typography>
            </Box>
            {server.message && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Message:
                </Typography>
                <Typography variant="caption">{server.message}</Typography>
              </Box>
            )}
          </Box>
        ))
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            ml: 2,
          }}
        >
          No server data available. Click &quot;Health Check&quot; to start
          monitoring.
        </Typography>
      )}
    </Stack>
  );
};
